import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAuthStore } from "../store/authStore";
import { API_BASE } from "../services/api";

/**
 * Mapeia tipos de evento do backend para chaves de query do React Query
 * que devem ser invalidadas. Mantém em um lugar só pra ser fácil evoluir.
 */
function chavesAfetadas(tipo: string, _payload: any): string[][] {
  if (tipo.startsWith("tarefa.")) {
    return [["tarefas"], ["tarefa"], ["tarefa-ajustes"], ["tempo-ajustes"], ["tempo-ajustes-pendentes"], ["dashboard"], ["projeto-anexos"], ["projeto"]];
  }
  if (tipo.startsWith("projeto.comentario")) {
    return [["projeto-comentarios"], ["cliente-projeto"]];
  }
  if (tipo.startsWith("projeto.anexo")) {
    return [["projeto-anexos"], ["cliente-projeto"]];
  }
  if (tipo.startsWith("projeto.")) {
    return [["projetos"], ["projeto"], ["dashboard"], ["cliente-projetos"], ["cliente-projeto"]];
  }
  if (tipo === "notificacao.nova") {
    return [["notificacoes"], ["notificacoes-count"]];
  }
  if (tipo === "chat.mensagem" || tipo === "chat.mensagem.editada" || tipo === "chat.mensagem.removida") {
    return [["chat-mensagens"], ["chat-conversas"]];
  }
  if (tipo === "chat.conversa") {
    return [["chat-conversas"], ["chat-conversa"]];
  }
  if (tipo === "chat.lido") {
    // Outro participante leu — atualiza ultimaLeituraEm na conversa pra recalcular vistinhos
    return [["chat-conversa"], ["chat-conversas"]];
  }
  if (tipo.startsWith("parcela.") || tipo.startsWith("contrato.")) {
    return [["contratos"], ["mensalidades"], ["parcelas-vencidas"], ["dashboard"]];
  }
  if (tipo.startsWith("cliente.")) {
    return [["clientes"], ["dashboard"]];
  }
  if (tipo === "usuario.tarefa-atual") {
    // `tarefa` invalida pra atualizar o "ocupante" no drawer aberto;
    // `chat-conversa` invalida pra atualizar a pílula do outro no header do chat DM;
    // `historico-sessoes` pra a aba Histórico refletir a sessão recém aberta/fechada.
    return [["me"], ["trabalhando-agora"], ["tarefa"], ["chat-conversa"], ["historico-sessoes"]];
  }
  if (tipo.startsWith("usuario.")) {
    return [["usuarios"], ["usuarios-ativos"]];
  }
  return [];
}

/**
 * Conecta no /api/eventos/stream via SSE e invalida queries afetadas
 * conforme os eventos chegam. Reconecta automaticamente em queda.
 */
export function useEventStream() {
  const qc = useQueryClient();
  const token = useAuthStore((s) => s.token);
  const userId = useAuthStore((s) => s.user?.id);
  const esRef = useRef<EventSource | null>(null);

  useEffect(() => {
    if (!token || !userId) return;

    let stopped = false;
    let reconnectTimer: number | null = null;
    let attempts = 0;
    let openedAt = 0;

    // Backoff exponencial: 3s, 6s, 12s, 24s, max 30s. Jitter evita "thundering herd"
    // quando várias abas/dispositivos reconectam após um restart do backend.
    function computeDelay() {
      const base = Math.min(30_000, 3_000 * Math.pow(2, Math.max(0, attempts - 1)));
      return base + Math.random() * 500;
    }

    function scheduleReconnect(reason: string) {
      if (stopped) return;
      // Aba oculta: não reconecta agora — espera visibilitychange
      if (typeof document !== "undefined" && document.hidden) {
        console.warn(`[SSE] reconexão adiada (aba oculta) — ${reason}`);
        return;
      }
      const delay = computeDelay();
      console.warn(`[SSE] reconectando em ${Math.round(delay)}ms (tentativa ${attempts + 1}, ${reason})`);
      reconnectTimer = window.setTimeout(connect, delay);
    }

    function connect() {
      if (stopped) return;
      reconnectTimer = null;
      attempts += 1;
      openedAt = 0;
      // EventSource não suporta header customizado — usa query token
      const apiBase = API_BASE.startsWith("/")
        ? `${window.location.origin}${API_BASE}`
        : API_BASE;
      const url = `${apiBase}/eventos/stream?token=${encodeURIComponent(token!)}`;
      const es = new EventSource(url);
      esRef.current = es;

      es.onopen = () => {
        openedAt = Date.now();
      };

      const handler = (tipo: string) => (e: MessageEvent) => {
        let payload: any = null;
        try { payload = JSON.parse(e.data); } catch {}
        const chaves = chavesAfetadas(tipo, payload);
        for (const k of chaves) {
          qc.invalidateQueries({ queryKey: k });
        }
        // Re-emite no window para hooks específicos (chat, presence, etc.)
        try { window.dispatchEvent(new CustomEvent(`growsoft:${tipo}`, { detail: payload })); } catch {}
        if (tipo === "chat.mensagem") {
          try { window.dispatchEvent(new CustomEvent("growsoft:chat-mensagem", { detail: payload })); } catch {}
        }
        if (tipo === "presence.online" || tipo === "presence.offline") {
          qc.invalidateQueries({ queryKey: ["presence"] });
        }
      };

      // Subscribe nos tipos conhecidos
      const tipos = [
        "tarefa.criada", "tarefa.atualizada", "tarefa.removida", "tarefa.movida",
        "tarefa.feedback", "tarefa.anexo", "tarefa.aprovacao",
        "projeto.criado", "projeto.atualizado", "projeto.removido",
        "projeto.comentario", "projeto.anexo",
        "cliente.criado", "cliente.atualizado",
        "usuario.criado", "usuario.atualizado", "usuario.tarefa-atual",
        "parcela.paga", "contrato.criado",
        "notificacao.nova",
        "chat.mensagem", "chat.mensagem.editada", "chat.mensagem.removida", "chat.conversa", "chat.digitando", "chat.lido",
        "presence.online", "presence.offline",
      ];
      tipos.forEach((t) => es.addEventListener(t, handler(t) as any));

      es.onerror = () => {
        const lifetime = openedAt ? Date.now() - openedAt : 0;
        const state = es.readyState;
        es.close();
        esRef.current = null;
        if (stopped) return;
        // Conexão sobreviveu o suficiente pra ser considerada "estável" — zera o backoff.
        if (lifetime > 30_000) attempts = 0;
        // Avisa imediatamente que a rede pode estar fora (banner reage)
        try { window.dispatchEvent(new CustomEvent("growsoft:rede-falhou", { detail: { src: "sse" } })); } catch {}
        scheduleReconnect(`readyState=${state}, lifetime=${lifetime}ms`);
      };
    }

    function onVisibility() {
      if (stopped) return;
      if (document.hidden) return;
      // Ficou visível e não há conexão ativa — reconecta agora, sem esperar backoff.
      if (!esRef.current) {
        if (reconnectTimer) {
          window.clearTimeout(reconnectTimer);
          reconnectTimer = null;
        }
        connect();
      }
    }

    document.addEventListener("visibilitychange", onVisibility);
    connect();

    return () => {
      stopped = true;
      document.removeEventListener("visibilitychange", onVisibility);
      if (reconnectTimer) window.clearTimeout(reconnectTimer);
      esRef.current?.close();
      esRef.current = null;
    };
  }, [token, userId, qc]);
}
